"use client";

import {
  updateSchedule,
  blockedSchedule,
  createSchedule,
} from "@/store/modules/schedules/thunks";
import { FullCalendarWrapperProps, Professional } from "@/types";
import { DayHours } from "@/types/setting/index";
import { buildAvatarSrc } from "@/utils/buildAvatarSrc";
import { formatarTelefoneMundial } from "@/utils/formatarTelefoneMundial";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import resourceTimeGridPlugin from "@fullcalendar/resource-timegrid";
import { format } from "date-fns";
import { Star, User } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Input, Select, Button } from "@/components";
import "react-datepicker/dist/react-datepicker.css";
import { Modal } from "..";
import { FormGroup } from "../form";
import * as S from "./styles";
import { toast } from "react-toastify";

type WeekDay =
  | "sunday"
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday";

const FullCalendarWrapper: React.FC<
  FullCalendarWrapperProps & {
    onInfoClick?: () => void;
    onOpenHoursModal?: () => void;
  }
> = ({
  establishment,
  setting,
  professionals,
  events,
  currentDate,
  onCurrentDateChange,
  isSelectable = false,
  handleEventClick,
  handleDateSelect,
  services,
  dict,
  isBloqueMode,
  setIsBloqueMode,
  fetchSchedule,
  dispatch,
  schedules,
  updateLoadingState,
  onInfoClick,
  onOpenHoursModal,
  handleBlockedTime,
}) => {
  const calendarRef = useRef<FullCalendar | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [interval_minutes, setIntervalMinutes] = useState<string>("00:15:00");
  const [openModalResource, setOpenModalResource] = useState(false);
  const [openModalCalendar, setOpenModalCalendar] = useState(false);
  const [openBloqueioPorDataModal, setOpenBloqueioPorDataModal] =
    useState(false);
  const [isSubmittingBloqueio, setIsSubmittingBloqueio] = useState(false);
  const [employees, setEmployees] = useState<Professional[]>(
    professionals || [],
  );
  const [selectedResources, setSelectedResources] = useState<string[]>(
    professionals && professionals.length > 0
      ? professionals.map((p) => String(p.id))
      : [],
  );

  const modalCalendarRef = useRef<FullCalendar | null>(null);
  const [selectedModalDate, setSelectedModalDate] = useState<Date | null>(null);
  const [modalTitle, setModalTitle] = useState<string>("");

  const [dragSnapshot, setDragSnapshot] = useState<{
    eventId: string | null;
    oldStart: Date | null;
    oldEnd: Date | null;
    oldResourceId: string | null;
  } | null>(null);

  const [pendingDrop, setPendingDrop] = useState<{
    info: any;
    eventId: string | null;
    oldStart: Date | null;
    oldEnd: Date | null;
    newStart: Date | null;
    newEnd: Date | null;
    oldResourceId: string | null;
    newResourceId: string | null;
  } | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [updatingEventId, setUpdatingEventId] = useState<string | null>(null);

  const [dayGridPluginMod, setDayGridPluginMod] = useState<any>(null);

  const [hoverTooltip, setHoverTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    data: {
      title?: string;
      phone?: string;
      timeRange?: string;
      services?: string;
      products?: string;
      description?: string;
    } | null;
  }>({ visible: false, x: 0, y: 0, data: null });

  // Track per-event mousemove handlers to clean up on leave
  const mouseMoveHandlersRef = useRef<Map<string, (e: MouseEvent) => void>>(
    new Map(),
  );

  useEffect(() => {
    if (openModalCalendar && !dayGridPluginMod) {
      import("@fullcalendar/daygrid").then((mod) =>
        setDayGridPluginMod(mod.default),
      );
    }
  }, [openModalCalendar]);

  useEffect(() => {
    try {
      const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
      const isi = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
      setIsIOS(Boolean(isi));
    } catch (e) {
      setIsIOS(false);
    }
  }, []);

  const isValidResource = useCallback(
    (resourceId?: string | null) =>
      !!employees.find((p) => String(p.id) === String(resourceId)),
    [employees],
  );

  useEffect(() => {
    if (!openModalCalendar) return;
    setTimeout(() => {
      const title = modalCalendarRef.current?.getApi()?.view?.title;
      if (title) setModalTitle(title);
    }, 0);
  }, [openModalCalendar]);

  const toggleResource = (id: string) => {
    setSelectedResources((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  };

  const filteredResources = useMemo(() => {
    if (!employees || employees.length === 0) {
      return [];
    }

    return employees.filter((p) => selectedResources.includes(String(p.id)));
  }, [employees, selectedResources]);

  const parseOrObject = (input: string | null | undefined): DayHours | null => {
    if (!input) return null;
    try {
      return JSON.parse(input);
    } catch {
      return null;
    }
  };

  const rawBusinessHours = setting?.business_hours;

  const businessHours: Record<WeekDay, DayHours | null> = useMemo(
    () => ({
      sunday: parseOrObject(rawBusinessHours?.sunday),
      monday: parseOrObject(rawBusinessHours?.monday),
      tuesday: parseOrObject(rawBusinessHours?.tuesday),
      wednesday: parseOrObject(rawBusinessHours?.wednesday),
      thursday: parseOrObject(rawBusinessHours?.thursday),
      friday: parseOrObject(rawBusinessHours?.friday),
      saturday: parseOrObject(rawBusinessHours?.saturday),
    }),
    [rawBusinessHours],
  );

  const getBusinessHoursForDate = useCallback(
    (date: Date) => {
      const days: WeekDay[] = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const dayKey = days[date.getDay()];
      const hours = businessHours[dayKey];

      return {
        start: hours?.start,
        end: hours?.end,
      };
    },
    [businessHours],
  );

  const initialTimes = useMemo(
    () =>
      getBusinessHoursForDate(currentDate ? new Date(currentDate) : new Date()),
    [currentDate, getBusinessHoursForDate],
  );

  const [slotTimes, setSlotTimes] = useState({
    start:
      initialTimes.start && initialTimes.start !== "00:00:00"
        ? initialTimes.start
        : "08:00:00",
    end:
      initialTimes.end && initialTimes.end !== "00:00:00"
        ? initialTimes.end === "00:00"
          ? "24:00"
          : initialTimes.end
        : "20:00:00",
  });

  useEffect(() => {
    if (currentDate) {
      const times = getBusinessHoursForDate(new Date(currentDate));
      setSlotTimes({
        start:
          times.start && times.start !== "00:00:00" ? times.start : "08:00:00",
        end:
          times.end && times.end !== "00:00:00"
            ? times.end === "00:00"
              ? "24:00"
              : times.end
            : "20:00:00",
      });
    }
  }, [currentDate, getBusinessHoursForDate]);

  const handleDatesSet = useCallback(
    (arg: any) => {
      const times = getBusinessHoursForDate(new Date(arg.start));
      setSlotTimes({
        start:
          times.start && times.start !== "00:00:00" ? times.start : "08:00:00",
        end:
          times.end && times.end !== "00:00:00"
            ? times.end === "00:00"
              ? "24:00"
              : times.end
            : "20:00:00",
      });
      if (onCurrentDateChange) {
        onCurrentDateChange(new Date(arg.start));
      }

      // When user navigates the calendar (prev/next/today), refresh schedules
      // so the displayed events match the selected date range.
      try {
        if (typeof fetchSchedule === "function") {
          // fire-and-forget; the parent handles loading state
          void fetchSchedule(new Date(arg.start));
        }
      } catch (e) {
        // ignore
      }
    },
    [getBusinessHoursForDate, onCurrentDateChange],
  );

  function minutesToHHMMSS(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    const hh = String(hours).padStart(2, "0");
    const mm = String(mins).padStart(2, "0");

    return `${hh}:${mm}:00`;
  }

  const handleEventDragStart = useCallback((arg: any) => {
    try {
      const ev = arg.event;
      const oldRes = (ev.getResources && ev.getResources()[0]?.id) ?? null;
      setDragSnapshot({
        eventId: ev.id ?? null,
        oldStart: ev.start ?? null,
        oldEnd: ev.end ?? null,
        oldResourceId: oldRes,
      });
    } catch {
      setDragSnapshot(null);
    }
  }, []);

  const handleEventResize = useCallback(
    async (info: any) => {
      if (isBloqueMode) {
        info.revert();
        return;
      }

      const event = info.event;
      const status = event.extendedProps?.status;

      if (status === "finalized") {
        info.revert();
        return;
      }

      const eventId = event.id ?? null;
      const newStart = event.start ?? null;
      const newEnd = event.end ?? null;
      const resourceId =
        (event.getResources && event.getResources()[0]?.id) ?? null;

      const oldStart =
        info.oldEvent && info.oldEvent.start ? info.oldEvent.start : null;
      const oldEnd =
        info.oldEvent && info.oldEvent.end ? info.oldEvent.end : null;

      setPendingDrop({
        info,
        eventId,
        oldStart,
        oldEnd,
        newStart,
        newEnd,
        oldResourceId: resourceId,
        newResourceId: resourceId,
      });

      setConfirmOpen(true);
    },
    [isBloqueMode],
  );

  const handleEventDrop = useCallback(
    async (info: any) => {
      if (isBloqueMode) {
        info.revert();
        return;
      }

      const event = info.event;
      const status = event.extendedProps?.status;
      if (status === "finalized") {
        info.revert();
        return;
      }
      const eventId = event.id ?? null;
      const newStart = event.start ?? null;
      const newEnd = event.end ?? null;
      const newResourceId =
        (event.getResources && event.getResources()[0]?.id) ?? null;

      const oldStart =
        dragSnapshot?.oldStart ??
        (info.oldEvent && info.oldEvent.start) ??
        null;
      const oldEnd =
        dragSnapshot?.oldEnd ?? (info.oldEvent && info.oldEvent.end) ?? null;
      const oldResourceId =
        dragSnapshot?.oldResourceId ??
        (info.oldResource && info.oldResource.id) ??
        null;

      if (newResourceId && !isValidResource(newResourceId)) {
        info.revert();
        setDragSnapshot(null);
        return;
      }

      setPendingDrop({
        info,
        eventId,
        oldStart,
        oldEnd,
        newStart,
        newEnd,
        oldResourceId,
        newResourceId,
      });
      setConfirmOpen(true);
    },
    [isBloqueMode, dragSnapshot, isValidResource],
  );

  const formatDateTime = (d: Date | null) =>
    d
      ? d.toLocaleString("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "";

  const handleConfirmMove = useCallback(async () => {
    if (!pendingDrop) return;
    const { info, eventId, newStart, newEnd, newResourceId } = pendingDrop;

    setConfirmOpen(false);

    try {
      updateLoadingState("updating", true);
      setUpdatingEventId(String(eventId));
      const eventData = schedules?.data?.find(
        (s: any) => String(s.id) === String(eventId),
      ) as any;

      if (!newStart || !newEnd) {
        throw new Error("Datas de início ou fim inválidas");
      }

      const formatDateToISO = (date: Date | null): string => {
        if (!date) return "";
        return format(date, "yyyy-MM-dd'T'HH:mm");
      };

      const formatDateTime = (date: Date | null): string => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      };

      const payload = {
        id: Number(eventId),
        start_date_time: formatDateToISO(newStart),
        closing_date_time: formatDateTime(newEnd),
        user_id: newResourceId ? Number(newResourceId) : null,
        type: "blocked",
        status: eventData?.status || "",
        client_id: eventData?.client_id || null,
      };

      await dispatch(updateSchedule(payload as any));

      try {
        const ev = info.event;
        ev.setExtendedProp("type", "encaixe");
      } catch {
        console.error("Erro ao definir tipo de evento");
      }

      if (typeof fetchSchedule === "function") {
        await fetchSchedule();
      }
    } catch (err) {
      console.error("Erro ao mover evento:", err);
      try {
        info.revert();
      } catch {
        // ignore
      }
    } finally {
      setPendingDrop(null);
      setDragSnapshot(null);
      setUpdatingEventId(null);
      updateLoadingState("updating", false);
    }
  }, [pendingDrop, schedules, dispatch, fetchSchedule, updateLoadingState]);

  const handleCancelMove = useCallback(() => {
    try {
      pendingDrop?.info?.revert?.();
    } catch {
      // ignore
    } finally {
      setConfirmOpen(false);
      setPendingDrop(null);
      setDragSnapshot(null);
    }
  }, [pendingDrop]);

  useEffect(() => {
    if (setting?.schedule_setting?.interval_minutes) {
      const interval = setting.schedule_setting.interval_minutes;
      setIntervalMinutes(minutesToHHMMSS(interval));
    }
  }, [setting?.schedule_setting?.interval_minutes]);

  useEffect(() => {
    if (professionals?.length > 0) {
      setEmployees(professionals);
      const resourceIds = professionals.map((p) => String(p.id));
      if (selectedResources.length === 0) {
        setSelectedResources(resourceIds);
      }
    }
  }, [professionals, selectedResources.length]);

  const formatTime = useCallback((date: Date | null) => {
    if (!date) return "";
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  // Timezone used for interpreting naive datetimes from the API. Prefer
  // establishment timezone (IANA), fallback to Sao_Paulo.
  // const timezone = establishment?.timezone || "America/Sao_Paulo";
  // console.log("[calendar] timezone:", timezone);

  // Calculate a fixed calendar height based on visible slot time range so
  // spacing remains consistent regardless of `slotDuration` or available
  // hours (prevents the calendar from compressing when fewer hours are shown).
  const calendarSizes = useMemo(() => {
    const parseHHMM = (t: string) => {
      if (!t) return 0;
      const [hh, mm] = t.split(":");
      return Number(hh || 0) + Number(mm || 0) / 60;
    };

    const start = parseHHMM(slotTimes.start || "08:00:00");
    const end = parseHHMM(slotTimes.end || "20:00:00");
    const visibleHours = Math.max(1, end - start);

    // pixels per hour - tune this number to change vertical density
    const pxPerHour =
      ((setting as any)?.schedule_setting?.px_per_hour as number) || 100;

    // header/toolbars + small buffer
    const headerExtra = 120;

    // total height used for content (without header)
    const contentHeight = Math.max(240, Math.round(visibleHours * pxPerHour));

    return {
      contentHeight,
      totalHeight: contentHeight + headerExtra,
      headerExtra,
    };
  }, [
    slotTimes.start,
    slotTimes.end,
    (setting as any)?.schedule_setting?.px_per_hour,
  ]);

  // Normalize incoming events to ensure iOS/Safari-friendly date objects
  // and sanitize titles/descriptions to avoid embedded newlines/control chars.
  const normalizedEvents = useMemo(() => {
    if (!events) return [];
    try {
      return events.map((ev: any) => {
        const copy = { ...(ev || {}) } as any;

        const parseDateValue = (v: any): Date | string | null => {
          if (!v) return null;
          if (v instanceof Date) return v;
          if (typeof v === "number") return new Date(v);
          if (typeof v !== "string") return null;

          let s = String(v).trim();

          // Normalize space separator to T
          s = s.replace(
            /^(\d{4}-\d{2}-\d{2})[ ]+(\d{2}:\d{2}(:\d{2})?)/,
            "$1T$2",
          );

          // If the string contains explicit timezone (Z or +hh:mm/-hh:mm),
          // let the Date parser handle it (produces absolute instant)
          if (/[zZ]$/.test(s) || /[+-]\d{2}(:?\d{2})?$/.test(s)) {
            const parsed = new Date(s);
            if (!Number.isNaN(parsed.getTime())) return parsed;
          }

          // If no timezone info, keep the ISO-like string (YYYY-MM-DDTHH:mm[:ss])
          // and let FullCalendar interpret it using the calendar timezone.
          const m = s.match(
            /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/,
          );
          if (m) {
            try {
              const year = Number(m[1]);
              const month = Number(m[2]) - 1;
              const day = Number(m[3]);
              const hour = Number(m[4]);
              const minute = Number(m[5]);
              const second = m[6] ? Number(m[6]) : 0;
              // Build a local Date instance with the same wall-clock time
              const localDate = new Date(
                year,
                month,
                day,
                hour,
                minute,
                second,
              );
              if (!Number.isNaN(localDate.getTime())) return localDate;
            } catch (e) {
              // ignore
            }
          }

          // Date only (YYYY-MM-DD) — treat as local midnight
          if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
            try {
              const parts = s.split("-");
              const year = Number(parts[0]);
              const month = Number(parts[1]) - 1;
              const day = Number(parts[2]);
              const localDate = new Date(year, month, day, 0, 0, 0);
              if (!Number.isNaN(localDate.getTime())) return localDate;
            } catch (e) {
              // ignore
            }
          }

          // Fallback to Date parse
          const fallback = new Date(s);
          if (!Number.isNaN(fallback.getTime())) return fallback;

          return null;
        };

        const originalStartVal = copy.start;
        const originalEndVal = copy.end;
        const parsedStart = parseDateValue(originalStartVal);
        const parsedEnd = parseDateValue(originalEndVal);
        if (parsedStart) copy.start = parsedStart;
        if (parsedEnd) copy.end = parsedEnd;

        // Debugging help: only in non-production show raw vs parsed values
        try {
          if (process.env.NODE_ENV !== "production") {
            console.debug("[calendar] event parse:", {
              id: copy.id,
              originalStartVal,
              parsedStart,
              originalEndVal,
              parsedEnd,
            });
          }
        } catch (e) {
          // ignore
        }

        if (typeof copy.title === "string") {
          copy.title = copy.title.replace(/[\r\n]+/g, " ").trim();
        }

        if (
          copy.extendedProps &&
          typeof copy.extendedProps.description === "string"
        ) {
          copy.extendedProps = {
            ...copy.extendedProps,
            description: copy.extendedProps.description
              .replace(/[\r\n]+/g, " ")
              .trim(),
          };
        }

        return copy;
      });
    } catch (e) {
      return events as any;
    }
  }, [events]);

  return (
    <div>
      <S.FullCalendarGlobalStyles />
      <div
        style={
          {
            "--fc-resource-count": String(filteredResources?.length || 1),
          } as React.CSSProperties
        }
      >
        <FullCalendar
          timeZone={"local"}
          schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
          longPressDelay={100}
          ref={calendarRef}
          themeSystem="standard"
          plugins={[resourceTimeGridPlugin, interactionPlugin]}
          initialView="resourceTimeGridDay"
          headerToolbar={{
            left: "prev,next calendario",
            center: "today title",
            right: "filtro bloquear atualizar bloqueioPorData info horarios",
          }}
          eventAllow={(dropInfo, draggedEvent) => {
            if (!draggedEvent) return false;
            const status = (draggedEvent.extendedProps as any)?.status;
            if (status === "finalized") return false;
            return true;
          }}
          customButtons={{
            filtro: {
              text: "Filtro",
              click: () => {
                setOpenModalResource(!openModalResource);
              },
            },
            calendario: {
              text: "Calendário",
              click: () => {
                setOpenModalCalendar(!openModalCalendar);
              },
            },
            bloquear: {
              text: isBloqueMode ? "Cancelar Bloqueio" : "Bloquear",
              click: () => setIsBloqueMode(!isBloqueMode),
            },
            atualizar: {
              text: "Atualizar",
              click: async () => {
                updateLoadingState("updating", true);
                await fetchSchedule();
                updateLoadingState("updating", false);
              },
            },
            bloqueioPorData: {
              text: "Bloqueio por Data",
              click: () => {
                setOpenBloqueioPorDataModal(true);
              },
            },
            info: {
              text: "Info",
              click: () => {
                if (onInfoClick) onInfoClick();
              },
            },
            horarios: {
              text: "⏰",
              click: () => {
                try {
                  if (onOpenHoursModal) onOpenHoursModal();
                } catch (e) {}
              },
            },
          }}
          resources={filteredResources}
          locale={ptBrLocale}
          events={events ? normalizedEvents : []}
          expandRows={false}
          slotDuration={interval_minutes}
          slotLabelInterval={interval_minutes}
          slotLabelFormat={{
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }}
          datesSet={handleDatesSet}
          eventContent={(arg) => {
            const event = arg.event;
            const bgColor = event.backgroundColor || "var(--lightBlue)";
            const borderColor = event.borderColor || "var(--blue)";
            const start = event.start;
            const end = event.end;
            const isPacked = event.extendedProps?.isPacked ? true : false;
            const subscriptionStatus = event.extendedProps?.subscriptionStatus;
            const phone = event.extendedProps?.phone
              ? formatarTelefoneMundial(event.extendedProps?.phone)
              : "";
            const eventId = String(event.id ?? "");
            const isUpdating = updatingEventId === eventId;

            const formatTime = (date: Date) =>
              date?.toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
              });

            const desiredCut = event.extendedProps?.desiredCut || "";
            const description = event.extendedProps?.description || "";
            const products: Array<{ name: string; quantity: number }> =
              Array.isArray(event.extendedProps?.products)
                ? event.extendedProps?.products.map((p: any) => ({
                    name: p?.name ?? "Produto",
                    quantity: Number(p?.quantity ?? 1),
                  }))
                : [];
            const productsSummary = products
              .map(
                (p) => `${p.name}${p.quantity > 1 ? `(${p.quantity}x)` : ""}`,
              )
              .join(", ");

            const intervalToMinutes = (s?: string) => {
              if (!s) return 0;
              const parts = String(s).split(":");
              const hh = Number(parts[0] || 0);
              const mm = Number(parts[1] || 0);
              return hh * 60 + mm;
            };

            const eventDurationMinutes =
              start && end
                ? Math.round((end.getTime() - start.getTime()) / 60000)
                : 0;
            const nameInFront =
              intervalToMinutes(interval_minutes) >= 30 &&
              eventDurationMinutes >= 30;

            return (
              <div
                className="fc-custom-event relative group h-full w-full"
                style={{
                  backgroundColor: bgColor,
                  borderLeft: `${borderColor}`,
                  overflow: "hidden",
                }}
              >
                <div className="flex items-center justify-end gap-1 absolute top-1 right-1">
                  {subscriptionStatus === "active" && (
                    <div className="">
                      {subscriptionStatus === "active" && (
                        <div>
                          <Star
                            className="text-yellow-500 font-extrabold"
                            size={16}
                          />
                        </div>
                      )}
                      {subscriptionStatus === "expired" && (
                        <div>
                          <Star
                            className="text-red-500 font-extrabold"
                            size={16}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {isPacked && (
                    <div className="flex items-center justify-end">📦</div>
                  )}
                </div>
                {nameInFront ? (
                  <div className="flex items-center justify-start gap-2 pr-6">
                    {start && end && (
                      <div className="text-green-900 font-semibold text-xs whitespace-nowrap">
                        {formatTime(start)} - {formatTime(end)}
                      </div>
                    )}
                    <div className="flex items-center gap-2 ml-2">
                      <div className="font-medium text-xs leading-tight text-gray-800 truncate">
                        {isUpdating ? (
                          <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-3/4 relative overflow-hidden">
                              <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/50 to-transparent animate-shimmer"></div>
                            </div>
                          </div>
                        ) : (
                          event.title
                        )}
                      </div>
                      {phone && (
                        <div className="text-gray-600 text-xs whitespace-nowrap">
                          • {phone}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Horário */}
                    <div className="flex items-center justify-between gap-1 text-green-900 font-semibold">
                      {start && end && (
                        <span>
                          {formatTime(start)} - {formatTime(end)}
                        </span>
                      )}
                    </div>
                    {/* Nome */}
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-xs leading-tight text-gray-800 truncate">
                        {isUpdating ? (
                          <div className="animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-3/4 relative overflow-hidden">
                              <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/50 to-transparent animate-shimmer"></div>
                            </div>
                          </div>
                        ) : (
                          event.title
                        )}
                      </div>
                    </div>
                    {/* Telefone padrão (quando condição não estiver ativa) */}
                    {phone && !isUpdating && (
                      <div className="text-xs text-gray-600 break-words -mt-0.5">
                        {phone}
                      </div>
                    )}
                  </>
                )}
                {/* Corte desejado */}
                {desiredCut && !isUpdating && (
                  <div className="text-[10px] leading-tight text-gray-700 break-words -mt-0.5">
                    {desiredCut}
                  </div>
                )}
                {/* Produtos */}
                {productsSummary && !isUpdating && (
                  <div className="text-[10px] leading-tight text-gray-700 break-words -mt-0.5">
                    {productsSummary}
                  </div>
                )}
                {/* Descrição */}
                {description && !isUpdating && (
                  <div className="text-[10px] leading-tight text-gray-500 break-words -mt-0.5">
                    {description}
                  </div>
                )}
              </div>
            );
          }}
          eventDidMount={(arg) => {
            try {
              const el = arg.el as HTMLElement;
              const handler = (e: TouchEvent) => {
                try {
                  e.stopPropagation();
                } catch {}
                try {
                  handleEventClick?.({
                    el,
                    event: arg.event,
                    jsEvent: e,
                    view: arg.view,
                  } as any);
                } catch {}
              };
              (el as any).__fc_touch_handler = handler;
              el.addEventListener("touchend", handler, { passive: true });
            } catch {}
          }}
          eventWillUnmount={(arg) => {
            try {
              const el = arg.el as HTMLElement;
              const handler = (el as any).__fc_touch_handler;
              if (handler)
                el.removeEventListener("touchend", handler as EventListener);
              delete (el as any).__fc_touch_handler;
            } catch {}
          }}
          eventMouseEnter={(info) => {
            const event = info.event;
            const start = event.start;
            const end = event.end;
            const phone = event.extendedProps?.phone
              ? formatarTelefoneMundial(event.extendedProps?.phone)
              : "";
            const formatTime = (date: Date) =>
              date?.toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
              });
            const desiredCut = event.extendedProps?.desiredCut || "";
            const description = event.extendedProps?.description || "";
            const products: Array<{ name: string; quantity: number }> =
              Array.isArray(event.extendedProps?.products)
                ? event.extendedProps?.products.map((p: any) => ({
                    name: p?.name ?? "Produto",
                    quantity: Number(p?.quantity ?? 1),
                  }))
                : [];
            const productsSummary = products
              .map(
                (p) => `${p.name}${p.quantity > 1 ? `(${p.quantity}x)` : ""}`,
              )
              .join(", ");
            const timeRange =
              start && end ? `${formatTime(start)} - ${formatTime(end)}` : "";

            // Show tooltip
            setHoverTooltip({
              visible: true,
              x: info.jsEvent?.clientX ? info.jsEvent.clientX + 12 : 0,
              y: info.jsEvent?.clientY ? info.jsEvent.clientY + 12 : 0,
              data: {
                title: event.title,
                phone,
                timeRange,
                services: desiredCut,
                products: productsSummary,
                description,
              },
            });

            // Track mouse movement across the entire event element
            const handler = (e: MouseEvent) => {
              setHoverTooltip((prev) =>
                prev.visible
                  ? { ...prev, x: e.clientX + 12, y: e.clientY + 12 }
                  : prev,
              );
            };
            mouseMoveHandlersRef.current.set(String(event.id), handler);
            info.el.addEventListener("mousemove", handler);
          }}
          eventMouseLeave={(info) => {
            const event = info.event;
            const handler = mouseMoveHandlersRef.current.get(String(event.id));
            if (handler) {
              try {
                info.el.removeEventListener("mousemove", handler);
              } catch {
                // ignore
              }
              mouseMoveHandlersRef.current.delete(String(event.id));
            }
            setHoverTooltip({ visible: false, x: 0, y: 0, data: null });
          }}
          initialDate={currentDate}
          selectable={isSelectable}
          selectMirror
          allDaySlot={false}
          stickyHeaderDates={false}
          eventClick={handleEventClick}
          select={(info) => {
            calendarRef.current?.getApi().unselect();
            handleDateSelect?.(info);
          }}
          slotMinTime={slotTimes.start}
          slotMaxTime={slotTimes.end}
          nowIndicator
          height={"80vh"}
          editable={true}
          eventResourceEditable={true}
          eventStartEditable={true}
          eventDurationEditable={true}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          resourceLabelContent={(arg) => {
            const prof = employees?.find(
              (p) => String(p.id) === String(arg.resource.id),
            );
            const avatar = prof
              ? buildAvatarSrc({
                  avatar_url: (prof as any).avatar_url,
                  avatar_path: (prof as any).avatar_path,
                  legacy: (prof as any).legacy_img,
                })
              : "";
            return (
              <div className="flex gap-2 items-center h-9">
                {avatar ? (
                  <img
                    className="w-7 h-7 rounded-full object-cover"
                    src={avatar}
                    alt={prof?.title ?? "avatar"}
                  />
                ) : (
                  <User className="w-7 h-7 rounded-full" />
                )}
                <div>
                  <span className="font-semibold">
                    {prof?.title?.split(" ")[0]}
                  </span>
                </div>
              </div>
            );
          }}
        />
      </div>

      {hoverTooltip.visible &&
        hoverTooltip.data &&
        typeof document !== "undefined" &&
        // Render tooltip in a portal so it is not clipped/stacked under transformed ancestors (fixes iOS Safari issues)
        (() => {
          const tooltipWidth = 384; // w-96
          const tooltipHeight = 256; // max-h-64
          let adjustedX = hoverTooltip.x;
          let adjustedY = hoverTooltip.y;

          if (typeof window !== "undefined") {
            if (adjustedX + tooltipWidth > window.innerWidth) {
              adjustedX = window.innerWidth - tooltipWidth - 10;
            }
            if (adjustedY + tooltipHeight > window.innerHeight) {
              adjustedY = window.innerHeight - tooltipHeight - 10;
            }
            if (adjustedX < 10) adjustedX = 10;
            if (adjustedY < 10) adjustedY = 10;
          }

          return createPortal(
            <div
              style={{
                position: "fixed",
                left: adjustedX,
                top: adjustedY,
                zIndex: 99999999,
                transform: "translateZ(0)",
                WebkitTransform: "translateZ(0)",
              }}
              className="pointer-events-none w-96 max-w-xs p-3 rounded-md shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-100 overflow-auto max-h-64"
            >
              {hoverTooltip.data.title && (
                <div className="text-sm font-semibold mb-2">
                  {hoverTooltip.data.title}
                </div>
              )}

              {/* Tooltip stacked vertically */}
              {hoverTooltip.data.phone && (
                <div className="text-xs text-gray-600 mb-1 w-full truncate">
                  {hoverTooltip.data.phone}
                </div>
              )}

              {hoverTooltip.data.timeRange && (
                <div className="text-xs text-gray-600 mb-1 w-full truncate">
                  {hoverTooltip.data.timeRange}
                </div>
              )}

              {hoverTooltip.data.services && (
                <div className="text-[11px] text-gray-700 dark:text-gray-200 mb-1 w-full truncate">
                  Serviços: {hoverTooltip.data.services}
                </div>
              )}

              {hoverTooltip.data.products && (
                <div className="text-[11px] text-gray-700 dark:text-gray-200 mb-1 w-full truncate">
                  Produtos: {hoverTooltip.data.products}
                </div>
              )}

              {hoverTooltip.data.description && (
                <div className="text-[11px] text-gray-600 dark:text-gray-300 mt-1 break-words">
                  {hoverTooltip.data.description}
                </div>
              )}
            </div>,
            document.body,
          );
        })()}

      <Modal
        titleModal="Confirmar movimentação"
        open={confirmOpen}
        onOpenChange={() => {
          setConfirmOpen(false);
          setPendingDrop(null);
        }}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {pendingDrop?.oldResourceId === pendingDrop?.newResourceId ? (
              <>
                Você tem certeza que deseja alterar o{" "}
                <strong>horário/duração</strong> deste evento? Após a alteração
                será considerado um <strong>encaixe</strong> e não nos
                responsabilizamos por conflitos.
              </>
            ) : (
              <>
                Você tem certeza que deseja mover este evento? Após a alteração
                será considerado um <strong>encaixe</strong> e não nos
                responsabilizamos por conflitos.
              </>
            )}
          </p>
          <div className="text-sm bg-gray-50 p-3 rounded">
            <div className="font-medium">
              {pendingDrop?.oldResourceId === pendingDrop?.newResourceId
                ? "Alteração de Horário/Duração"
                : "Movimentação"}
            </div>
            <div className="mt-1">
              <div>
                <strong>De:</strong>{" "}
                {formatDateTime(pendingDrop?.oldStart ?? null)} -{" "}
                {formatTime(pendingDrop?.oldEnd ?? null)}
              </div>
              <div>
                <strong>Para:</strong>{" "}
                {formatDateTime(pendingDrop?.newStart ?? null)} -{" "}
                {formatTime(pendingDrop?.newEnd ?? null)}
              </div>
              {pendingDrop?.oldStart &&
                pendingDrop?.newStart &&
                pendingDrop?.oldEnd &&
                pendingDrop?.newEnd && (
                  <div className="mt-2 text-xs bg-blue-50 p-2 rounded">
                    <strong>Duração:</strong>
                    <span className="ml-1">
                      {Math.round(
                        (new Date(pendingDrop.oldEnd).getTime() -
                          new Date(pendingDrop.oldStart).getTime()) /
                          (1000 * 60),
                      )}{" "}
                      min →{" "}
                      {Math.round(
                        (new Date(pendingDrop.newEnd).getTime() -
                          new Date(pendingDrop.newStart).getTime()) /
                          (1000 * 60),
                      )}{" "}
                      min
                    </span>
                  </div>
                )}
            </div>
            {pendingDrop?.oldResourceId !== pendingDrop?.newResourceId && (
              <div className="mt-2 text-xs text-gray-500">
                Profissional atual:{" "}
                <span className="font-medium">
                  {employees.find(
                    (e) => String(e.id) === String(pendingDrop?.oldResourceId),
                  )?.title ?? "-"}
                </span>
                <br />
                Profissional destino:{" "}
                <span className="font-medium">
                  {employees.find(
                    (e) => String(e.id) === String(pendingDrop?.newResourceId),
                  )?.title ?? "-"}
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              className="px-3 py-1 rounded border"
              onClick={handleCancelMove}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="px-3 py-1 rounded bg-green-600 text-white"
              onClick={handleConfirmMove}
            >
              {pendingDrop?.oldResourceId === pendingDrop?.newResourceId
                ? "Confirmar alteração"
                : "Confirmar movimento (encaixe)"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        titleModal={dict["Select Professionals"]}
        open={openModalResource}
        onOpenChange={() => setOpenModalResource(false)}
      >
        <FormGroup className="flex flex-col gap-2 space-y-2">
          {employees?.map((prof) => (
            <label
              key={prof.id}
              className="flex items-center gap-3 p-2 border rounded-lg cursor-pointer hover:bg-gray-50 transition"
            >
              <input
                type="checkbox"
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                checked={selectedResources.includes(prof.id)}
                onChange={() => toggleResource(prof.id)}
              />
              <span className="text-gray-700 font-medium text-sm">
                {prof.title}
              </span>
            </label>
          ))}
        </FormGroup>
      </Modal>

      <Modal
        titleModal={""}
        open={openModalCalendar}
        onOpenChange={() => setOpenModalCalendar(false)}
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                onClick={() => modalCalendarRef.current?.getApi().prev()}
              >
                ‹
              </button>
              <button
                type="button"
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                onClick={() => modalCalendarRef.current?.getApi().next()}
              >
                ›
              </button>
            </div>

            <div className="flex-1 text-center">
              <h3 className="text-xl font-semibold text-(--textColor)">
                {modalTitle ||
                  new Date().toLocaleDateString("pt-BR", {
                    month: "long",
                    year: "numeric",
                  })}
              </h3>
            </div>

            <button
              type="button"
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
              onClick={() => modalCalendarRef.current?.getApi().today()}
            >
              {dict?.Today || "Hoje"}
            </button>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {dayGridPluginMod ? (
              <FullCalendar
                schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
                ref={modalCalendarRef}
                initialView="dayGridMonth"
                plugins={[dayGridPluginMod, interactionPlugin]}
                headerToolbar={false}
                locale={ptBrLocale}
                height={400}
                selectable={false}
                fixedWeekCount={false}
                showNonCurrentDates={false}
                dateClick={(arg) => {
                  const date = new Date(arg.date);
                  setSelectedModalDate(date);
                  if (calendarRef.current)
                    calendarRef.current.getApi().gotoDate(date);
                  setOpenModalCalendar(false);
                }}
                datesSet={(arg) => {
                  setModalTitle(arg.view.title || "");
                }}
                dayHeaderFormat={{ weekday: "short" }}
                dayCellClassNames={(arg) => {
                  const today = new Date();
                  const cellDate = new Date(arg.date);
                  const isToday =
                    cellDate.toDateString() === today.toDateString();
                  const isSelected =
                    selectedModalDate &&
                    cellDate.toDateString() ===
                      selectedModalDate.toDateString();

                  return [
                    "cursor-pointer hover:bg-blue-50 transition-colors",
                    isToday && "bg-blue-100 font-semibold",
                    isSelected && "bg-blue-200",
                  ]
                    .filter(Boolean)
                    .join(" ");
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-40 text-sm text-gray-500">
                Carregando calendário...
              </div>
            )}
          </div>

          {selectedModalDate && (
            <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
              <span className="text-sm font-medium text-gray-700">
                Data selecionada:{" "}
                {selectedModalDate.toLocaleDateString("pt-BR")}
              </span>
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                onClick={() => setSelectedModalDate(null)}
              >
                Limpar seleção
              </button>
            </div>
          )}
        </div>
      </Modal>

      {/* Bloqueio por Data Modal */}
      <Modal
        open={openBloqueioPorDataModal}
        onOpenChange={setOpenBloqueioPorDataModal}
        titleModal={
          dict?.["Block by Date"] ||
          dict?.["Bloqueio por Data"] ||
          "Bloqueio por Data"
        }
      >
        <form
          className="space-y-4 p-2"
          onSubmit={async (e) => {
            e.preventDefault();
            const form = e.currentTarget as HTMLFormElement;
            const fd = new FormData(form);
            const employee = fd.get("employee");
            const startDate = fd.get("start_date");
            const startTime = fd.get("start_time");
            const endDate = fd.get("end_date");
            const endTime = fd.get("end_time");

            // Basic required-field validation: block submit if any field missing
            if (
              !employee ||
              String(employee).trim() === "" ||
              !startDate ||
              String(startDate).trim() === "" ||
              !startTime ||
              String(startTime).trim() === "" ||
              !endDate ||
              String(endDate).trim() === "" ||
              !endTime ||
              String(endTime).trim() === ""
            ) {
              toast.error("Preencha todos os campos obrigatórios.");
              return;
            }

            const formattedStart = `${String(startDate)}T${String(startTime)}`;
            const formattedEnd = `${String(endDate)}T${String(endTime)}`;

            // Ensure start < end
            try {
              const s = new Date(formattedStart);
              const eDate = new Date(formattedEnd);
              if (Number.isNaN(s.getTime()) || Number.isNaN(eDate.getTime())) {
                toast.error("Datas/horários inválidos.");
                return;
              }
              if (s >= eDate) {
                toast.error(
                  "Data/hora inicial deve ser anterior à data/hora final.",
                );
                return;
              }
            } catch {
              toast.error("Datas/horários inválidos.");
              return;
            }

            const payload: any = {
              user_id: employee ? Number(employee) : null,
              start_date_time: formattedStart,
              closing_date_time: formattedEnd,
              type: "blocked",
            };

            console.log("[Bloqueio por Data] payload:", payload);
            setIsSubmittingBloqueio(true);
            try {
              if (handleBlockedTime) {
                const result = await handleBlockedTime(
                  payload,
                  setIsBloqueMode,
                );
                if (result) {
                  toast.success("Horário bloqueado com sucesso.");
                  if (typeof fetchSchedule === "function")
                    await fetchSchedule();
                  setIsSubmittingBloqueio(false);
                  setOpenBloqueioPorDataModal(false);
                  return;
                }
                // If parent's handler didn't perform the block, fall through to fallback
              }

              console.log("[Bloqueio por Data] fallback check", {
                schedules: schedules?.data?.length ?? 0,
                schedulesData: schedules?.data,
                establishmentId: establishment?.id,
                employeesCount: employees?.length ?? 0,
              });

              const targetScheduleId =
                schedules?.data?.find((it: any) =>
                  establishment?.id
                    ? it?.establishment_id === establishment.id
                    : true,
                )?.id ??
                schedules?.data?.[0]?.id ??
                null;

              if (targetScheduleId) {
                try {
                  const res = await dispatch(
                    blockedSchedule({
                      data: payload,
                      scheduleId: targetScheduleId,
                    } as any),
                  );
                  if ((res as any)?.error) {
                    console.error("blockedSchedule rejected:", res);
                    toast.error("Erro ao bloquear horário.");
                  } else {
                    toast.success("Horário bloqueado com sucesso.");
                    if (typeof fetchSchedule === "function")
                      await fetchSchedule();
                    // Close modal on success
                    setOpenBloqueioPorDataModal(false);
                  }
                } catch (err) {
                  console.error("Erro ao bloquear (dispatch):", err);
                  toast.error("Erro ao bloquear horário.");
                }
              } else {
                console.log(
                  "[Bloqueio por Data] no targetScheduleId, will try createSchedule fallback",
                  {
                    schedules: schedules?.data,
                    payload,
                  },
                );

                try {
                  // create a new schedule with type blocked as a fallback
                  const createPayload: any = {
                    ...payload,
                    // ensure minimal required fields for createSchedule
                    type: payload.type || "blocked",
                    status: payload.status ?? "",
                  };

                  const resCreate: any = await dispatch(
                    createSchedule(createPayload as any),
                  );
                  if (resCreate?.error) {
                    console.error("createSchedule rejected:", resCreate);
                    toast.error("Erro ao bloquear horário.");
                  } else {
                    toast.success("Horário bloqueado com sucesso.");
                    if (typeof fetchSchedule === "function")
                      await fetchSchedule();
                    setOpenBloqueioPorDataModal(false);
                  }
                } catch (err) {
                  console.error(
                    "Erro ao criar agendamento para bloqueio:",
                    err,
                  );
                  toast.error(
                    "Não foi possível determinar o agendamento alvo para bloqueio.",
                  );
                } finally {
                  setIsSubmittingBloqueio(false);
                }
              }
            } catch (err) {
              console.error("Erro ao executar bloqueio:", err);
              toast.error("Erro ao bloquear horário.");
            } finally {
              setIsSubmittingBloqueio(false);
            }
          }}
        >
          <div className="text-sm text-gray-600 font-medium">
            Todos os campos são obrigatórios.
          </div>
          <FormGroup>
            <Select
              label={
                dict?.["Employee"] || dict?.["Funcionário"] || "Funcionário"
              }
              name="employee"
              options={employees.map((e) => ({ value: e.id, label: e.title }))}
              placeholder={
                dict?.["Select employee"] || "Selecione o funcionário"
              }
            />
          </FormGroup>
          <FormGroup>
            <Input
              label={
                dict?.["Start Date"] || dict?.["Data Inicial"] || "Data Inicial"
              }
              name="start_date"
              type="date"
            />
          </FormGroup>
          <FormGroup>
            <Input
              label={dict?.["End Date"] || dict?.["Data Final"] || "Data Final"}
              name="end_date"
              type="date"
            />
          </FormGroup>
          <FormGroup>
            <Input
              label={
                dict?.["Start Time"] ||
                dict?.["Horário Inicial"] ||
                "Horário Inicial"
              }
              name="start_time"
              type="time"
            />
          </FormGroup>
          <FormGroup>
            <Input
              label={
                dict?.["End Time"] || dict?.["Horário Final"] || "Horário Final"
              }
              name="end_time"
              type="time"
            />
          </FormGroup>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              text={dict?.["Cancel"] || dict?.["Cancelar"] || "Cancelar"}
              variant="default"
              onClick={() => setOpenBloqueioPorDataModal(false)}
              type="button"
              disabled={isSubmittingBloqueio}
            />
            <Button
              text={dict?.["Block"] || dict?.["Bloquear"] || "Bloquear"}
              variant="success"
              type="submit"
              isLoading={isSubmittingBloqueio}
              disabled={isSubmittingBloqueio}
              onClick={() =>
                console.log("[Bloqueio por Data] submit button clicked")
              }
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default FullCalendarWrapper;
